import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setModalState, modalViewsConst } from "../../store/modalSlice";
import { markWelcomeShown, markStepComplete } from "../../store/clubChecklistSlice";
import ChecklistStep from "../Checklist/ChecklistStep";
import WelcomeScreen from "../Checklist/WelcomeScreen";
import { usePostHogCapture } from "../../hooks/usePostHogCapture";
import { POSTHOG_EVENTS } from "../../utils/posthogEvents";
import LoadingIcon from "../LoadingIcon";
import ModalHeader from "./ModalHeader";
import { ReactComponent as GiftIcon } from "../../assets/icons/gift-new.svg";
import { ReactComponent as CheckmarkIcon } from "../../assets/icons/checkmark-new.svg";
import { RootState, AppDispatch } from "../../store";
import {
  CHECKLIST_STEPS,
  CHECKLIST_TOTAL_COUNT,
  CHECKLIST_TITLE,
  getChecklistCompletedCount,
  hasAnyStepComplete,
} from "../../utils/checklist";

interface ChecklistModalProps {
  onClose: () => void;
}

const ChecklistModal = ({ onClose }: ChecklistModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const { capture } = usePostHogCapture();
  const checklist = useSelector((state: RootState) => state.clubChecklist.data);
  const loading = useSelector((state: RootState) => state.clubChecklist.loading);
  const error = useSelector((state: RootState) => state.clubChecklist.error);
  const personas = useSelector((state: RootState) => state.personas.personas);

  const handleStep2Click = useCallback(() => {
    const hasPersonas = Object.keys(personas || {}).length > 0;
    if (hasPersonas) dispatch(markStepComplete(2));
  }, [personas, dispatch]);

  const completedCount = checklist ? getChecklistCompletedCount(checklist) : 0;
  const totalCount = CHECKLIST_TOTAL_COUNT;

  // Track modal open
  useEffect(() => {
    capture(POSTHOG_EVENTS.CHECKLIST_OPENED);
  }, []);

  // If any step was already completed before going through the flow, skip the welcome screen
  useEffect(() => {
    if (!checklist || checklist.welcomeShown) return;
    if (hasAnyStepComplete(checklist)) dispatch(markWelcomeShown());
  }, [checklist]);

  // When hat is unlocked, open the hat unlock modal
  useEffect(() => {
    if (checklist?.hatUnlocked && !checklist?.shippingAddress) {
      capture(POSTHOG_EVENTS.CHECKLIST_HAT_UNLOCKED);
      dispatch(
        setModalState({
          modalOpen: true,
          modalView: modalViewsConst.hatUnlock,
          properties: { checklistId: checklist.id },
        })
      );
    }
  }, [checklist?.hatUnlocked, checklist?.shippingAddress]);

  return (
    <div className="flex flex-col">
      <ModalHeader
        title={CHECKLIST_TITLE}
        icon={<GiftIcon className="h-5 w-5" />}
        onClose={onClose}
      />

      <div className="flex-1 min-h-0 overflow-y-auto">
        {loading === "pending" || loading === "idle" ? (
          <div className="flex items-center justify-center py-16">
            <LoadingIcon pixelSize={80} borderThick={6} fullPage={false} />
          </div>
        ) : loading === "failed" ? (
          <div className="flex items-center justify-center py-16 px-6">
            <p className="text-sm text-red-500 text-center font-Inter">
              {error ?? "Something went wrong loading your checklist. Please try again."}
            </p>
          </div>
        ) : (
          <div className="py-8 px-5">
            {/* Checklist header */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-eai-checklist-lt flex items-center justify-center flex-shrink-0 text-eai-primary-accent">
                <CheckmarkIcon className="w-4 h-4" style={{ "--stroke-0": "currentColor" } as any} />
              </div>
              <div>
                <h2 className="text-base font-bold text-gray-900 font-Inter">Checklist</h2>
                <p className="text-xs text-gray-400 font-Inter">{completedCount}/{totalCount} completed</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex gap-1.5 mb-7">
              {CHECKLIST_STEPS.map((step, index) => (
                <div
                  key={step.stepNumber}
                  className={`h-1.5 flex-1 rounded-full transition-colors ${
                    index < completedCount
                      ? "bg-eai-primary-accent"
                      : "bg-gray-200"
                  }`}
                />
              ))}
            </div>

            {/* Background action error banner */}
            {error && loading === "success" && (
              <div className="bg-red-50 border border-red-200 rounded-md px-3 py-2 mb-4">
                <p className="text-xs text-red-500 font-Inter">{error}</p>
              </div>
            )}

            {/* Welcome screen — only shown until dismissed */}
            {checklist && !checklist.welcomeShown ? (
              <WelcomeScreen />
            ) : (
              <>
                {/* Hat claimed banner */}
                {checklist?.shippingAddress && (
                  <div className="bg-eai-lt border border-eai-accent-lt rounded-xl p-4 mb-6 text-center">
                    <p className="text-sm font-semibold text-eai-primary-accent font-Inter">🎉 Your hat is on its way!</p>
                    <p className="text-xs text-gray-400 mt-1 font-Inter">We'll be in touch once it ships.</p>
                  </div>
                )}

                {/* Steps */}
                <div className="flex flex-col gap-5">
                  {CHECKLIST_STEPS.map((step, index) => (
                    <div key={step.stepNumber} className="relative">
                      <ChecklistStep
                        stepNumber={step.stepNumber}
                        description={step.description}
                        isComplete={checklist?.[step.stateKey] ?? false}
                        isLocked={step.isLocked ?? false}
                        ctaPath={step.ctaPath}
                        hints={step.hints}
                        onStepClick={step.stepNumber === 2 ? handleStep2Click : undefined}
                      />
                      {/* Connector line spanning the gap-5 (20px) to next card */}
                      {index < CHECKLIST_STEPS.length - 1 && (
                        <div
                          className={`absolute left-[27px] -bottom-5 w-px h-5 transition-colors ${
                            checklist?.[step.stateKey] ? "bg-eai-primary-accent" : "bg-gray-200"
                          }`}
                        />
                      )}
                    </div>
                  ))}
                </div>

                {/* Hat incentive message */}
                {!checklist?.hatUnlocked && !checklist?.shippingAddress && (
                  <p className="text-xs text-gray-500 text-center mt-6 font-Inter">
                    Complete all required steps to receive your Forecast Club hat!
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChecklistModal;
